import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addXP } from '../data/xp';
import ModuleSummary from '../components/ModuleSummary';
import { recordStart } from '../data/progress';
import { syncProgress } from '../api/public';

type Risk = 'low' | 'medium' | 'high';
type Choice = 'allow' | 'allow_while' | 'deny';
type Screen = 'intro' | 'playing' | 'app_summary' | 'final';

interface Permission {
  id: string;
  name: string;
  dialog: string;
  optimal: Choice;
  appGains: string[];
  teenLoses: string[];
  risk: Risk;
  explain: string;
}

interface AppScenario {
  id: string;
  name: string;
  category: string;
  tagline: string;
  permissions: Permission[];
}

interface AppResult {
  appId: string;
  appName: string;
  choices: Choice[];
  privacyScore: number;
  xpEarned: number;
}

const XP_COMPLETION = 80;
const MODULE_ID = 'permission-impossible';

const RISK_LABEL: Record<Risk, string> = { low: 'Low Risk', medium: 'Medium Risk', high: 'High Risk' };
const RISK_COLOR: Record<Risk, string> = { low: '#10b981', medium: '#f59e0b', high: '#ef4444' };
const CHOICE_LABEL: Record<Choice, string> = { allow: 'Allow', allow_while: 'Allow While Using', deny: 'Deny' };
const CHOICE_ORDER: Record<Choice, number> = { deny: 0, allow_while: 1, allow: 2 };

const RATINGS = [
  { min: 80, label: 'Privacy Champion', color: '#6366f1', description: 'You consistently recognised unnecessary permissions and protected your data.' },
  { min: 60, label: 'Cautious User', color: '#10b981', description: 'Mostly good choices — a few privacy red flags slipped through.' },
  { min: 40, label: 'Needs Work', color: '#f59e0b', description: 'You granted several unnecessary permissions. Worth reviewing device settings.' },
  { min: 0, label: 'Permission Novice', color: '#ef4444', description: 'Most apps got more access than they needed. Your data is working hard.' },
];

function calcScore(permissions: Permission[], choices: Choice[]): number {
  let score = 100;
  permissions.forEach((p, i) => {
    const c = choices[i];
    if (!c) return;
    const diff = CHOICE_ORDER[c] - CHOICE_ORDER[p.optimal];
    if (diff > 0) {
      const penalty = p.risk === 'high' ? 20 : p.risk === 'medium' ? 12 : 6;
      score -= penalty * diff;
    }
  });
  return Math.max(0, score);
}

function isGoodChoice(p: Permission, c: Choice): boolean {
  return CHOICE_ORDER[c] <= CHOICE_ORDER[p.optimal];
}

const APPS: AppScenario[] = [
  {
    id: 'lumio', name: 'Lumio', category: 'Utilities — Flashlight',
    tagline: 'The flashlight app that wants to know everything about you.',
    permissions: [
      {
        id: 'contacts', name: 'Contacts',
        dialog: 'Allow Lumio to access your contacts to "find friends who use Lumio"?',
        optimal: 'deny', risk: 'high',
        appGains: ['Your full contact list: names, numbers, emails', 'Mapping of your social relationships', 'Ability to upload your contacts to their servers'],
        teenLoses: ['Privacy of every person in your contacts who never agreed', 'Your social network handed to a flashlight app', 'Contact data potentially sold to data brokers'],
        explain: 'A flashlight has zero legitimate reason to access your contacts. This is a data grab — deny it every time.',
      },
      {
        id: 'location', name: 'Precise Location',
        dialog: 'Allow Lumio to access your location to "optimise brightness for your environment"?',
        optimal: 'deny', risk: 'high',
        appGains: ['Real-time GPS coordinates', 'History of every location where you use your flashlight', 'Your home, bedroom, and school addresses'],
        teenLoses: ['Physical location privacy at all times', 'Your daily patterns and routines revealed', 'Location data collected in sensitive private spaces'],
        explain: 'Brightness adjustment has no connection to GPS coordinates. This is surveillance disguised as a feature. Deny.',
      },
      {
        id: 'microphone', name: 'Microphone',
        dialog: 'Allow Lumio to access your microphone to "enable voice commands"?',
        optimal: 'deny', risk: 'high',
        appGains: ['Continuous microphone capability', 'Ambient audio from your surroundings', 'Voice pattern data that can identify you'],
        teenLoses: ['Privacy of all conversations near your phone', 'Audio from your bedroom, bathroom, and private spaces', 'A surveillance capability with no legitimate flashlight purpose'],
        explain: 'No flashlight needs microphone access. Voice commands are a pretext for surveillance. Deny and consider uninstalling.',
      },
    ],
  },
  {
    id: 'fitpulse', name: 'FitPulse', category: 'Health & Fitness',
    tagline: 'A fitness tracker — some permissions are reasonable, others are not.',
    permissions: [
      {
        id: 'motion', name: 'Motion & Fitness',
        dialog: 'Allow FitPulse to access Motion & Fitness data for step counting and workout detection?',
        optimal: 'allow', risk: 'low',
        appGains: ['Step count and movement patterns', 'Workout type detection (running, cycling)'],
        teenLoses: ['General fitness habits — low-sensitivity data proportionate for this app'],
        explain: 'This is the core feature. Motion data for a fitness app is proportionate. Allow it.',
      },
      {
        id: 'health', name: 'Health Data',
        dialog: 'Allow FitPulse to read and write Health data including heart rate and sleep?',
        optimal: 'allow_while', risk: 'medium',
        appGains: ['Heart rate, sleep duration, and health metrics', 'Ability to infer medical conditions over time', 'Sensitive biometric history'],
        teenLoses: ['Health data that could affect insurance if breached', 'Inferences about mental health and stress levels', 'Medical-grade data in a commercial company\'s hands'],
        explain: 'Health data is sensitive. "Allow While Using" is better than full always-on access — the app only needs it during active tracking.',
      },
      {
        id: 'location', name: 'Location While Using',
        dialog: 'Allow FitPulse to access your location to track outdoor workout routes?',
        optimal: 'allow_while', risk: 'medium',
        appGains: ['GPS route of each outdoor workout', 'Location when you exercise outside'],
        teenLoses: ['Your exercise routes and regular outdoor locations', 'Patterns about where and when you go outside'],
        explain: '"Allow While Using" is correct — a fitness app should track location during a run, but not 24/7. Never grant "Always On" for fitness apps.',
      },
      {
        id: 'contacts', name: 'Contacts',
        dialog: 'Allow FitPulse to access your contacts to "find friends for fitness challenges"?',
        optimal: 'deny', risk: 'medium',
        appGains: ['Full contact list including people who don\'t use FitPulse', 'Phone numbers uploaded to their servers for matching'],
        teenLoses: ['Privacy of everyone in your contacts who never agreed to this', 'Your social network mapped by a fitness company'],
        explain: 'Friends features don\'t require bulk contact upload — apps can use invite links. This is unnecessary data collection. Deny.',
      },
    ],
  },
  {
    id: 'vibe', name: 'Vibe', category: 'Social Media',
    tagline: 'Social apps need some permissions — but not all of them.',
    permissions: [
      {
        id: 'camera', name: 'Camera',
        dialog: 'Allow Vibe to access your camera to take photos and videos for posts?',
        optimal: 'allow_while', risk: 'low',
        appGains: ['Camera access when you\'re in the app'],
        teenLoses: ['Camera activated while the app is open — standard for social apps'],
        explain: '"Allow While Using" is correct. Camera access for posting is a core feature — just ensure it\'s not "Always On".',
      },
      {
        id: 'microphone', name: 'Microphone',
        dialog: 'Allow Vibe to access your microphone for video and voice posts?',
        optimal: 'allow_while', risk: 'medium',
        appGains: ['Audio capture during in-app recording', 'Voice data from your recording sessions'],
        teenLoses: ['Ambient audio while filming', 'In-app conversations audible to the microphone'],
        explain: '"Allow While Using" is appropriate if you use video features. Deny if you don\'t use voice or video posting.',
      },
      {
        id: 'contacts', name: 'Contacts',
        dialog: 'Allow Vibe to access your contacts to "find people you know"?',
        optimal: 'deny', risk: 'high',
        appGains: ['Full contact list uploaded to Vibe\'s servers', 'Phone number-based social graph of your relationships', 'Your contacts\' data used for targeting, even if they don\'t use Vibe'],
        teenLoses: ['Privacy of every person in your contacts who never agreed', 'Your social network mapped and potentially monetised', 'Your contacts targeted with ads based on their association with you'],
        explain: 'Social graphs built from contact lists are extremely valuable to advertisers. Your contacts never consented to being uploaded. Deny.',
      },
      {
        id: 'location', name: 'Precise Location',
        dialog: 'Allow Vibe to access your precise location to "show relevant local content"?',
        optimal: 'deny', risk: 'medium',
        appGains: ['Exact GPS coordinates while you browse the app', 'Neighbourhood and building-level location data', 'Your location correlated with content consumption patterns'],
        teenLoses: ['Your precise movements while scrolling a social feed', 'Home, school, and regular locations inferred over time'],
        explain: 'Approximate location is sufficient for local content. Precise GPS while browsing a social feed is disproportionate. Deny.',
      },
      {
        id: 'photos', name: 'Full Photo Library',
        dialog: 'Allow Vibe full access to your entire photo library?',
        optimal: 'allow_while', risk: 'low',
        appGains: ['Access to your entire photo library, including private photos', 'Ability to scan and analyse all images'],
        teenLoses: ['All photos accessible to the app, including ones not meant for posting'],
        explain: 'Modern devices offer "Selected Photos" access — only grant specific photos. If full access is the only option, "Allow While Using" is better than "Always".',
      },
    ],
  },
  {
    id: 'studybuddy', name: 'StudyBuddy', category: 'Education',
    tagline: 'Some requests are reasonable — others are not what they seem.',
    permissions: [
      {
        id: 'camera', name: 'Camera',
        dialog: 'Allow StudyBuddy to access your camera to scan assignments and documents?',
        optimal: 'allow_while', risk: 'low',
        appGains: ['Camera access for document scanning when app is open'],
        teenLoses: ['Camera activated while in-app — reasonable for the stated purpose'],
        explain: 'Document scanning is a legitimate feature. "Allow While Using" keeps it proportionate.',
      },
      {
        id: 'microphone', name: 'Microphone',
        dialog: 'Allow StudyBuddy to access your microphone for voice-to-text note taking?',
        optimal: 'allow_while', risk: 'medium',
        appGains: ['Audio recording during note-taking sessions', 'Voice data from your study environment'],
        teenLoses: ['Ambient audio captured while doing homework — could include private conversations nearby'],
        explain: '"Allow While Using" is reasonable if you use voice notes. Be aware: microphone access captures ambient audio, not just your voice.',
      },
      {
        id: 'contacts', name: 'Contacts',
        dialog: 'Allow StudyBuddy to access your contacts to "invite classmates to study groups"?',
        optimal: 'deny', risk: 'medium',
        appGains: ['Full contact list including family — not just classmates', 'Phone numbers uploaded for user-matching'],
        teenLoses: ['Privacy of everyone you know (family, friends) who never agreed', 'Your contacts uploaded to an education company without their consent'],
        explain: 'An invite link achieves the same goal without bulk contact upload. This permission is unnecessary. Deny.',
      },
      {
        id: 'clipboard', name: 'Clipboard Access',
        dialog: 'Allow StudyBuddy to access your clipboard to "paste assignment text directly"?',
        optimal: 'deny', risk: 'high',
        appGains: ['Everything you have copied: text, links, passwords, messages', 'Clipboard monitoring happens invisibly in the background', 'Sensitive content from other apps readable if recently copied'],
        teenLoses: ['Privacy of anything you copy-paste across all apps', 'Security of passwords or sensitive data in your clipboard', 'Awareness — this access is silent with no visible indicator'],
        explain: 'Clipboard access is one of the most invasive silent permissions. Apps can read recently copied text — including passwords. Deny.',
      },
    ],
  },
  {
    id: 'snapedit', name: 'SnapEdit', category: 'Photography & Editing',
    tagline: 'Two permissions make sense. Three do not.',
    permissions: [
      {
        id: 'photos', name: 'Photo Library',
        dialog: 'Allow SnapEdit to access your photos to edit images from your library?',
        optimal: 'allow_while', risk: 'low',
        appGains: ['Access to your photo library for editing'],
        teenLoses: ['Your photo library accessible while in-app — proportionate for a photo editor'],
        explain: 'Photo access is the core feature. "Allow While Using" or "Selected Photos" is appropriate.',
      },
      {
        id: 'camera', name: 'Camera',
        dialog: 'Allow SnapEdit to access your camera to take photos to edit immediately?',
        optimal: 'allow_while', risk: 'low',
        appGains: ['Camera access within the editing app'],
        teenLoses: ['Camera activated during editing sessions'],
        explain: 'Taking a photo to immediately edit is a legitimate feature. "Allow While Using" is correct.',
      },
      {
        id: 'microphone', name: 'Microphone',
        dialog: 'Allow SnapEdit to access your microphone to "enable voice-activated filters"?',
        optimal: 'deny', risk: 'high',
        appGains: ['Continuous microphone capability far beyond voice commands', 'Audio data captured during all editing sessions', 'Ambient audio from your surroundings while you edit photos'],
        teenLoses: ['Privacy of all conversations near your phone while editing', 'Audio surveillance capability disguised as a convenience feature'],
        explain: 'A photo editor has no legitimate need for microphone access. "Voice-activated filters" is a pretext for an unnecessary permission. Deny.',
      },
      {
        id: 'location', name: 'Precise Location',
        dialog: 'Allow SnapEdit to access your precise location to geotag photos?',
        optimal: 'deny', risk: 'medium',
        appGains: ['Exact GPS coordinates embedded in every photo you edit', 'Your precise home, school, and personal locations tied to your photos'],
        teenLoses: ['Location privacy — edited photos can expose your address if shared', 'Precise location history linked to all your creative activity'],
        explain: 'If you want geotagging, use approximate location or add it manually. Precise GPS in a photo editor is disproportionate and a risk if photos are shared.',
      },
      {
        id: 'contacts', name: 'Contacts',
        dialog: 'Allow SnapEdit to access your contacts to "suggest friends to share edits with"?',
        optimal: 'deny', risk: 'medium',
        appGains: ['Full contact list for sharing suggestions', 'Phone numbers uploaded for a feature available without this permission'],
        teenLoses: ['Privacy of all your contacts', 'Bulk contact collection for a feature the OS share sheet already provides'],
        explain: 'You can share photos with anyone via the native share sheet — SnapEdit doesn\'t need your contact list for this. Deny.',
      },
    ],
  },
  {
    id: 'skyalert', name: 'SkyAlert', category: 'Weather',
    tagline: '"Location Always On" is never acceptable for a weather app.',
    permissions: [
      {
        id: 'location_always', name: 'Location (Always On)',
        dialog: 'Allow SkyAlert to always access your location, even when the app is closed?',
        optimal: 'deny', risk: 'high',
        appGains: ['Your precise location 24/7 — even when your phone is in your pocket or you\'re asleep', 'Complete movement tracking: when you leave home, where you go, how long you stay', 'Your home address, school, workplace, and all regular locations built over time'],
        teenLoses: ['Complete location privacy at all times of day and night', 'The ability to move without being tracked by a weather company', 'Location data during private moments: sleeping, medical appointments, personal spaces'],
        explain: '"Always On" location is almost never justified. For weather, set your location manually or allow it "While Using." "Always On" is surveillance. Deny.',
      },
      {
        id: 'location_precise', name: 'Precise Location',
        dialog: 'Allow SkyAlert to access your precise GPS location for accurate local weather?',
        optimal: 'deny', risk: 'medium',
        appGains: ['Street-level GPS accuracy', 'More granular location data than any weather service requires'],
        teenLoses: ['Precise rather than general location tracking', 'Your exact street address inferrable from GPS data'],
        explain: 'Weather forecasts work at a city/neighbourhood level — approximate location is sufficient. Precise GPS is disproportionate for weather.',
      },
      {
        id: 'notifications', name: 'Notifications',
        dialog: 'Allow SkyAlert to send you severe weather alerts and daily forecasts?',
        optimal: 'allow', risk: 'low',
        appGains: ['Ability to send weather alerts and forecasts to your phone'],
        teenLoses: ['Notification access — standard and low-risk for a weather alert service'],
        explain: 'Push notifications are the core value proposition of a weather alert app. This permission is proportionate — allow it.',
      },
      {
        id: 'contacts', name: 'Contacts',
        dialog: 'Allow SkyAlert to access your contacts to "alert family members during severe weather"?',
        optimal: 'deny', risk: 'high',
        appGains: ['Full contact list for a weather app', 'Phone numbers of everyone you know uploaded to their servers', 'Social graph data with zero justification'],
        teenLoses: ['Privacy of all your contacts', 'Your entire address book handed to a weather service with no legitimate use'],
        explain: 'A weather app can let you share alerts via the OS share sheet. Contacts access for "family alerts" is a pretext. No weather service needs your contact list. Deny.',
      },
    ],
  },
];

export default function PermissionImpossible() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState<Screen>('intro');
  const [appIndex, setAppIndex] = useState(0);
  const [permIndex, setPermIndex] = useState(0);
  const [currentChoice, setCurrentChoice] = useState<Choice | null>(null);
  const [showConsequence, setShowConsequence] = useState(false);
  const [appChoices, setAppChoices] = useState<Choice[]>([]);
  const [results, setResults] = useState<AppResult[]>([]);
  const [sessionXP, setSessionXP] = useState(0);

  const currentApp = APPS[appIndex];
  const currentPerm = currentApp?.permissions[permIndex];

  function handleChoice(choice: Choice) {
    if (currentChoice !== null) return;
    setCurrentChoice(choice);
    setShowConsequence(true);
  }

  function handleNext() {
    const newChoices = [...appChoices, currentChoice!];
    const isLastPerm = permIndex >= currentApp.permissions.length - 1;
    if (!isLastPerm) {
      setAppChoices(newChoices);
      setPermIndex(p => p + 1);
      setCurrentChoice(null);
      setShowConsequence(false);
    } else {
      const score = calcScore(currentApp.permissions, newChoices);
      const xp = Math.max(20, Math.round(score * 0.6));
      addXP(xp);
      setResults(prev => [...prev, { appId: currentApp.id, appName: currentApp.name, choices: newChoices, privacyScore: score, xpEarned: xp }]);
      setSessionXP(prev => prev + xp);
      setAppChoices([]);
      setCurrentChoice(null);
      setShowConsequence(false);
      setPermIndex(0);
      setScreen('app_summary');
    }
  }

  function handleNextApp() {
    if (appIndex < APPS.length - 1) {
      setAppIndex(a => a + 1);
      setScreen('playing');
    } else {
      addXP(XP_COMPLETION, MODULE_ID);
      recordStart();
      const total = sessionXP + XP_COMPLETION;
      setSessionXP(total);
      syncProgress(MODULE_ID, total);
      setScreen('final');
    }
  }

  const lastResult = results[results.length - 1];
  const overallScore = results.length > 0 ? Math.round(results.reduce((s, r) => s + r.privacyScore, 0) / results.length) : 0;
  const rating = RATINGS.filter(r => overallScore >= r.min).pop()!;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0e1a', color: '#e2e8f0' }}>
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '20px 16px 48px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 10, color: '#6366f1', letterSpacing: '0.15em', textTransform: 'uppercase' as const }}>STRATEGY · ROLE-PLAY</div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#f8fafc', lineHeight: 1.2, marginTop: 2 }}>Permission Impossible</h1>
          </div>
          <div style={{ textAlign: 'right' as const }}>
            {screen === 'playing' && (
              <>
                <div style={{ fontSize: 11, color: '#64748b' }}>APP {appIndex + 1} / {APPS.length}</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Perm {permIndex + 1}/{currentApp.permissions.length}</div>
              </>
            )}
            <div style={{ fontSize: 11, color: '#ffa502', marginTop: 2 }}>+{sessionXP} XP</div>
          </div>
        </div>

        {/* ── Intro ── */}
        {screen === 'intro' && (
          <div style={{ animation: 'fadeIn 0.5s ease' }}>
            <div style={{ background: 'linear-gradient(135deg,rgba(99,102,241,0.08),rgba(168,85,247,0.08))', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 16, padding: '28px 24px', marginBottom: 24 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 }}>You are the Chief Privacy Officer</h2>
              <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.7, marginBottom: 10 }}>
                Six apps are requesting permissions on a fictional teen's phone. For each request, you decide: <strong style={{ color: '#e2e8f0' }}>Allow</strong>, <strong style={{ color: '#e2e8f0' }}>Allow While Using</strong>, or <strong style={{ color: '#e2e8f0' }}>Deny</strong>.
              </p>
              <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.7 }}>
                After each decision, you'll see exactly what the app gains — and what the teen loses. Your Privacy Score reflects how well you protected data without blocking legitimate features.
              </p>
            </div>
            <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
              <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 12 }}>Scoring</div>
              {[
                { label: 'Choosing the optimal permission level', xp: 'Full Privacy Score' },
                { label: 'Per-app XP based on Privacy Score', xp: 'Up to 60 XP' },
                { label: 'Module completion bonus', xp: '+80 XP' },
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < 2 ? '1px solid #1e293b' : 'none' }}>
                  <span style={{ fontSize: 13, color: '#cbd5e1' }}>{s.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#ffa502' }}>{s.xp}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setScreen('playing')} style={{ width: '100%', padding: 16, borderRadius: 12, background: 'linear-gradient(135deg,#6366f1,#7c3aed)', border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              Take Control of the Phone
            </button>
            <button onClick={() => navigate('/')} style={{ width: '100%', marginTop: 10, padding: 12, background: 'none', border: '1px solid #1e293b', borderRadius: 10, color: '#475569', fontSize: 14, cursor: 'pointer' }}>
              Back to Home
            </button>
          </div>
        )}

        {/* ── Playing ── */}
        {screen === 'playing' && currentPerm && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            {/* App banner */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9' }}>{currentApp.name}</div>
                <div style={{ fontSize: 12, color: '#64748b' }}>{currentApp.category}</div>
              </div>
              <div style={{ fontSize: 12, color: '#475569', maxWidth: 180, textAlign: 'right' as const, lineHeight: 1.4 }}>{currentApp.tagline}</div>
            </div>

            {/* Phone mock */}
            <div style={{ background: '#111827', border: '6px solid #1e293b', borderRadius: 32, padding: '18px 20px 24px', boxShadow: '0 20px 48px rgba(0,0,0,0.5)', marginBottom: showConsequence ? 20 : 0 }}>
              {/* Status bar */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18, padding: '0 4px' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>9:41</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                    {[3, 5, 7, 9].map((h, i) => <div key={i} style={{ width: 3, height: h, background: '#334155', borderRadius: 1 }} />)}
                  </div>
                  <div style={{ width: 14, height: 8, border: '1.5px solid #334155', borderRadius: 2, position: 'relative' as const }}>
                    <div style={{ position: 'absolute' as const, left: 1, top: 1, bottom: 1, width: '55%', background: '#10b981', borderRadius: 1 }} />
                  </div>
                </div>
              </div>

              {/* App icon + name */}
              <div style={{ textAlign: 'center' as const, marginBottom: 22 }}>
                <div style={{ width: 56, height: 56, background: 'linear-gradient(135deg,#6366f1,#7c3aed)', borderRadius: 14, margin: '0 auto 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 22, fontWeight: 900, color: 'white' }}>{currentApp.name[0]}</span>
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>{currentApp.name}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>would like to access</div>
              </div>

              {/* Permission dialog */}
              <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid #1e293b', borderRadius: 14, padding: '14px 18px', marginBottom: 18 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }}>{currentPerm.name}</div>
                <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}>{currentPerm.dialog}</div>
              </div>

              {/* Buttons or result */}
              {!currentChoice ? (
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
                  {(['allow', 'allow_while', 'deny'] as Choice[]).map(c => (
                    <button key={c} onClick={() => handleChoice(c)} style={{
                      padding: '12px 16px', borderRadius: 12, cursor: 'pointer', fontSize: 14, fontWeight: 600, textAlign: 'center' as const,
                      border: c === 'allow' ? '1.5px solid rgba(239,68,68,0.35)' : c === 'allow_while' ? '1.5px solid rgba(245,158,11,0.35)' : '1.5px solid rgba(99,102,241,0.35)',
                      background: c === 'allow' ? 'rgba(239,68,68,0.06)' : c === 'allow_while' ? 'rgba(245,158,11,0.06)' : 'rgba(99,102,241,0.06)',
                      color: c === 'allow' ? '#ef4444' : c === 'allow_while' ? '#f59e0b' : '#818cf8',
                    }}>
                      {CHOICE_LABEL[c]}
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{
                  padding: '12px 16px', borderRadius: 12, textAlign: 'center' as const, fontSize: 14, fontWeight: 700,
                  border: currentChoice === 'deny' ? '1.5px solid #6366f1' : currentChoice === 'allow_while' ? '1.5px solid #f59e0b' : '1.5px solid #ef4444',
                  background: currentChoice === 'deny' ? 'rgba(99,102,241,0.12)' : currentChoice === 'allow_while' ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
                  color: currentChoice === 'deny' ? '#818cf8' : currentChoice === 'allow_while' ? '#f59e0b' : '#ef4444',
                }}>
                  You chose: {CHOICE_LABEL[currentChoice]}
                </div>
              )}
            </div>

            {/* Consequence panel */}
            {showConsequence && currentChoice && (
              <div style={{ animation: 'slideUp 0.4s ease' }}>
                <div style={{
                  background: '#1e293b', borderRadius: 16, padding: 20, marginBottom: 14,
                  border: `1px solid ${isGoodChoice(currentPerm, currentChoice) ? 'rgba(99,102,241,0.3)' : 'rgba(239,68,68,0.25)'}`,
                }}>
                  {currentChoice !== 'deny' ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                      <div style={{ background: 'rgba(239,68,68,0.06)', borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 8 }}>App Gains</div>
                        {currentPerm.appGains.map((g, i) => <div key={i} style={{ fontSize: 11, color: '#fca5a5', lineHeight: 1.5, marginBottom: 3 }}>· {g}</div>)}
                      </div>
                      <div style={{ background: 'rgba(99,102,241,0.06)', borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ fontSize: 10, color: '#818cf8', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 8 }}>You Lose</div>
                        {currentPerm.teenLoses.map((l, i) => <div key={i} style={{ fontSize: 11, color: '#a5b4fc', lineHeight: 1.5, marginBottom: 3 }}>· {l}</div>)}
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8, fontWeight: 600 }}>What the app wanted:</div>
                      {currentPerm.appGains.map((g, i) => <div key={i} style={{ fontSize: 11, color: '#475569', lineHeight: 1.5, marginBottom: 3, textDecoration: 'line-through' }}>· {g}</div>)}
                      <div style={{ fontSize: 13, color: '#818cf8', fontWeight: 600, marginTop: 10 }}>Permission denied — data protected.</div>
                    </div>
                  )}
                  <div style={{ borderTop: '1px solid #334155', paddingTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 6, color: isGoodChoice(currentPerm, currentChoice) ? '#818cf8' : '#f59e0b' }}>
                      {isGoodChoice(currentPerm, currentChoice) ? 'Good choice' : `Better: ${CHOICE_LABEL[currentPerm.optimal]}`}
                    </div>
                    <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}>{currentPerm.explain}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: `${RISK_COLOR[currentPerm.risk]}15`, color: RISK_COLOR[currentPerm.risk], border: `1px solid ${RISK_COLOR[currentPerm.risk]}30` }}>
                      {RISK_LABEL[currentPerm.risk]}
                    </span>
                  </div>
                </div>
                <button onClick={handleNext} style={{ width: '100%', padding: 14, borderRadius: 12, background: 'linear-gradient(135deg,#6366f1,#7c3aed)', border: 'none', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  {permIndex < currentApp.permissions.length - 1 ? 'Next Permission' : 'See App Results'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── App Summary ── */}
        {screen === 'app_summary' && lastResult && (
          <div style={{ animation: 'fadeIn 0.5s ease' }}>
            <div style={{ textAlign: 'center' as const, marginBottom: 24 }}>
              <div style={{ fontSize: 11, color: '#6366f1', letterSpacing: '0.12em', textTransform: 'uppercase' as const, marginBottom: 6 }}>App Results</div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9' }}>{lastResult.appName}</h2>
            </div>
            <div style={{ background: '#1e293b', borderRadius: 16, padding: 24, marginBottom: 20, textAlign: 'center' as const }}>
              <div style={{ fontSize: 48, fontWeight: 800, color: lastResult.privacyScore >= 80 ? '#6366f1' : lastResult.privacyScore >= 60 ? '#10b981' : lastResult.privacyScore >= 40 ? '#f59e0b' : '#ef4444', lineHeight: 1 }}>
                {lastResult.privacyScore}
              </div>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>Privacy Score</div>
              <div style={{ fontSize: 14, color: '#ffa502', fontWeight: 700 }}>+{lastResult.xpEarned} XP earned</div>
            </div>

            {/* Decision review */}
            <div style={{ background: '#1e293b', borderRadius: 12, padding: '16px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 12 }}>Decision Review</div>
              {APPS[results.length - 1]?.permissions.map((p, i) => {
                const choice = lastResult.choices[i];
                const good = choice && isGoodChoice(p, choice);
                return (
                  <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < lastResult.choices.length - 1 ? '1px solid #0f172a' : 'none' }}>
                    <div style={{ fontSize: 13, color: '#cbd5e1' }}>{p.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, color: good ? '#818cf8' : '#f59e0b' }}>{choice ? CHOICE_LABEL[choice] : '—'}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: good ? '#10b981' : '#ef4444' }}>{good ? '✓' : '✗'}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <button onClick={handleNextApp} style={{ width: '100%', padding: 14, borderRadius: 12, background: 'linear-gradient(135deg,#6366f1,#7c3aed)', border: 'none', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              {appIndex < APPS.length - 1 ? `Next App: ${APPS[appIndex + 1].name}` : 'See Final Results'}
            </button>
          </div>
        )}

        {/* ── Final ── */}
        {screen === 'final' && (
          <div style={{ animation: 'fadeIn 0.6s ease' }}>
            <div style={{ textAlign: 'center' as const, marginBottom: 28 }}>
              <div style={{ fontSize: 11, color: rating.color, letterSpacing: '0.15em', textTransform: 'uppercase' as const, fontWeight: 700, marginBottom: 8 }}>Privacy Assessment Complete</div>
              <h2 style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>All 6 Apps Reviewed</h2>
            </div>

            <div style={{ background: `${rating.color}11`, border: `1px solid ${rating.color}33`, borderRadius: 16, padding: '24px 20px', textAlign: 'center' as const, marginBottom: 24 }}>
              <div style={{ fontSize: 52, fontWeight: 800, color: rating.color, lineHeight: 1 }}>{overallScore}</div>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>Overall Privacy Score</div>
              <div style={{ display: 'inline-block', background: `${rating.color}22`, border: `1px solid ${rating.color}44`, borderRadius: 100, padding: '4px 18px', fontSize: 13, fontWeight: 700, color: rating.color }}>
                {rating.label}
              </div>
              <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 12, lineHeight: 1.6 }}>{rating.description}</p>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#ffa502', marginTop: 8 }}>+{sessionXP} XP total</div>
            </div>

            <div style={{ background: '#1e293b', borderRadius: 12, padding: '16px 18px', marginBottom: 24 }}>
              <div style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginBottom: 12 }}>App Breakdown</div>
              {results.map((r, i) => (
                <div key={r.appId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < results.length - 1 ? '1px solid #0f172a' : 'none' }}>
                  <div style={{ fontSize: 13, color: '#cbd5e1' }}>{r.appName}</div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: r.privacyScore >= 80 ? '#6366f1' : r.privacyScore >= 60 ? '#10b981' : r.privacyScore >= 40 ? '#f59e0b' : '#ef4444', fontWeight: 700 }}>
                      {r.privacyScore}/100
                    </span>
                    <span style={{ fontSize: 12, color: '#ffa502' }}>+{r.xpEarned} XP</span>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#818cf8', marginBottom: 4 }}>Badge Earned: Permission Guardian</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>For completing all six app reviews and learning to read permission requests critically.</div>
            </div>

            <ModuleSummary
              moduleId="permissions"
              xpEarned={sessionXP}
              score={results.length > 0 ? Math.round(results.reduce((s, r) => s + r.privacyScore, 0) / results.length) : 0}
              scoreLabel="Avg Privacy Score"
            />
          </div>
        )}
      </div>
    </div>
  );
}
